import { EmployeeRole } from '@repo/types';

// RBAC Policies following the Access Control Matrix from admin-pfs.md
export const rbacPolicies = [
  // Job Scout permissions
  ['job_scout', 'dashboard', 'read'],
  ['job_scout', 'jobs', 'read'],
  ['job_scout', 'jobs:own', 'write'],
  ['job_scout', 'jobs:own', 'delete'],
  ['job_scout', 'profile:own', 'read'],
  ['job_scout', 'profile:own', 'write'],
  // Support is a shared queue: every staff role reads and replies. Closing a
  // ticket and reassigning someone else's are the manager-level actions, and
  // the API enforces that independently — this only shapes the UI.
  ['job_scout', 'support', 'read'],
  ['job_scout', 'support', 'write'],

  // Team Lead permissions (inherits Job Scout + additional)
  ['team_lead', 'job_scout', 'inherit'],
  ['team_lead', 'jobs', 'write'],
  ['team_lead', 'jobs', 'delete'],
  ['team_lead', 'jobs:bulk', 'write'],
  ['team_lead', 'jobs:duplicates', 'read'],
  ['team_lead', 'jobs:duplicates', 'write'],
  ['team_lead', 'employees', 'read'],
  ['team_lead', 'employees:own-team', 'write'],
  ['team_lead', 'performance:own-team', 'read'],

  // Manager permissions (inherits Team Lead + additional)
  ['manager', 'team_lead', 'inherit'],
  ['manager', 'employees', 'write'],
  ['manager', 'employees', 'delete'],
  ['manager', 'performance', 'read'],
  ['manager', 'customers', 'read'],
  ['manager', 'customers', 'write'],
  ['manager', 'customers:recommendations', 'read'],
  ['manager', 'customers:recommendations', 'write'],
  ['manager', 'analytics', 'read'],
  ['manager', 'support', 'manage'],

  // Admin permissions (full access)
  ['admin', 'manager', 'inherit'],
  ['admin', 'settings', 'read'],
  ['admin', 'settings', 'write'],
  ['admin', 'customers:billing', 'write'],
  ['admin', 'employees:roles', 'write'],
  ['admin', 'system', 'write'],
];

// Role hierarchy
export const roleHierarchy = [
  ['admin', 'manager'],
  ['manager', 'team_lead'],
  ['team_lead', 'job_scout'],
];

// Permission groups for easier management
export const permissionGroups = {
  dashboard: ['dashboard:read'],
  jobs: {
    read: ['jobs:read'],
    write: ['jobs:write', 'jobs:delete'],
    ownOnly: ['jobs:own:read', 'jobs:own:write', 'jobs:own:delete'],
    bulk: ['jobs:bulk:write'],
    duplicates: ['jobs:duplicates:read', 'jobs:duplicates:write'],
  },
  employees: {
    read: ['employees:read'],
    write: ['employees:write', 'employees:delete'],
    ownTeam: ['employees:own-team:write'],
    roles: ['employees:roles:write'],
  },
  performance: {
    read: ['performance:read'],
    ownTeam: ['performance:own-team:read'],
  },
  customers: {
    read: ['customers:read'],
    write: ['customers:write'],
    recommendations: ['customers:recommendations:read', 'customers:recommendations:write'],
    billing: ['customers:billing:write'],
  },
  analytics: {
    read: ['analytics:read'],
  },
  support: {
    read: ['support:read'],
    write: ['support:write'],
    manage: ['support:manage'],
  },
  settings: {
    read: ['settings:read'],
    write: ['settings:write'],
  },
};

// Helper function to get permissions for a role
export function getPermissionsForRole(role: EmployeeRole): string[] {
  const permissions: string[] = [];

  switch (role) {
    case EmployeeRole.JobScout:
      permissions.push(
        ...permissionGroups.dashboard,
        ...permissionGroups.jobs.read,
        ...permissionGroups.jobs.ownOnly,
        ...permissionGroups.support.read,
        ...permissionGroups.support.write
      );
      break;

    case EmployeeRole.TeamLead:
      permissions.push(
        ...permissionGroups.dashboard,
        ...permissionGroups.jobs.read,
        ...permissionGroups.jobs.write,
        ...permissionGroups.jobs.bulk,
        ...permissionGroups.jobs.duplicates,
        ...permissionGroups.employees.read,
        ...permissionGroups.employees.ownTeam,
        ...permissionGroups.performance.ownTeam,
        ...permissionGroups.support.read,
        ...permissionGroups.support.write
      );
      break;

    case EmployeeRole.Manager:
      permissions.push(
        ...permissionGroups.dashboard,
        ...permissionGroups.jobs.read,
        ...permissionGroups.jobs.write,
        ...permissionGroups.jobs.bulk,
        ...permissionGroups.jobs.duplicates,
        ...permissionGroups.employees.read,
        ...permissionGroups.employees.write,
        ...permissionGroups.performance.read,
        ...permissionGroups.customers.read,
        ...permissionGroups.customers.write,
        ...permissionGroups.customers.recommendations,
        ...permissionGroups.analytics.read,
        ...permissionGroups.support.read,
        ...permissionGroups.support.write,
        ...permissionGroups.support.manage
      );
      break;

    case EmployeeRole.Admin:
      permissions.push(
        ...permissionGroups.dashboard,
        ...permissionGroups.jobs.read,
        ...permissionGroups.jobs.write,
        ...permissionGroups.jobs.bulk,
        ...permissionGroups.jobs.duplicates,
        ...permissionGroups.employees.read,
        ...permissionGroups.employees.write,
        ...permissionGroups.employees.roles,
        ...permissionGroups.performance.read,
        ...permissionGroups.customers.read,
        ...permissionGroups.customers.write,
        ...permissionGroups.customers.recommendations,
        ...permissionGroups.customers.billing,
        ...permissionGroups.analytics.read,
        ...permissionGroups.settings.read,
        ...permissionGroups.settings.write,
        ...permissionGroups.support.read,
        ...permissionGroups.support.write,
        ...permissionGroups.support.manage
      );
      break;
  }

  return permissions;
}
